import { DEFAULT_PERMISSIONS, FSR_APPROVER_USER_TYPE } from '@/constants/constants';
import {
  categoriseFiles,
  formatFileSize,
  MAX_UPLOAD_BYTES,
  timePairValidator,
  totalSize,
  workedHours,
} from '@/helpers/file-size';
import { FSR, FSRAction, FSRDropdownOption } from '@/Interface/fsrManagementType';
import { PlantOwner } from '@/Interface/ticketManagementType';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';

/** Query params that are plain filters — cleared when null, and reset paging. */
const FILTER_KEYS = ['assignee', 'status', 'origin', 'job_visit_type', 'city'];

@Component({
  selector: 'app-fsr',
  templateUrl: './fsr.component.html',
  styleUrls: ['./fsr.component.scss'],
})
export class FsrComponent implements OnInit {
  // Keyed on the lowercased status label, same as the ticket list.
  statusBadgeMap: { [key: string]: string } = {
    unassigned: 'badge-warning',
    open: 'badge-cyan',
    assigned: 'badge-blue',
    'in progress': 'powder-blue',
    'on hold': 'badge-warning',
    'pending material': 'badge-warning',
    resolved: 'badge-success',
    closed: 'badge-gray',
    cancelled: 'badge-danger',
    reopened: 'badge-blue',
    approved: 'badge-success',
  };

  fsrList: FSR[] = [];

  // Pagination / filtering
  public pageSizeControl = new FormControl(10);
  public pageRecordOptions = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
    { value: '200', label: '200' },
  ];
  pageSize: number;
  pageToGo: number = 1;
  searchElement = '';
  next_page: number;
  current_page: number;
  previous_page: number;
  total_pages: number;
  total_items: number;
  private latestQueryParams: any = {};
  searchSubject: Subject<string> = new Subject();

  // Dropdown data
  plantList: any[] = [];
  statusFilterList: any[] = [];
  assignedUsersFilter: any[] = [];
  jobVisitTypes: FSRDropdownOption[] = [];
  complaintTypes: FSRDropdownOption[] = [];
  cityList: any[] = [];

  // Per-plant lists, refreshed on plant change
  customerList: PlantOwner[] = [];
  staffList: PlantOwner[] = [];

  // Forms
  fsrForm: FormGroup;
  assignForm: FormGroup;
  actionForm: FormGroup;

  // Attachments pending upload
  files: File[] = [];
  existingAttachments: any[] = [];

  // State
  loader = false;
  isLoadingTable = false;
  isSaving = false;
  isEditMode = false;
  selectedFsrId: number | null = null;
  selectedFsr: FSR | null = null;
  pendingAction: FSRAction | null = null;
  modalRef: NgbModalRef | null = null;
  userType: string | null = null;

  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady = false;

  // Oversized-upload warning state
  oversizeBreakdown: ReturnType<typeof categoriseFiles> | null = null;
  private oversizeModalRef: NgbModalRef | null = null;

  // Used by the oversize warning modal; the field set and its attachment
  // previews live in <app-fsr-form>.
  readonly formatFileSize = formatFileSize;

  constructor(
    private modalService: NgbModal,
    private _utilityService: UtilityService,
    private formBuilder: FormBuilder,
    private toaster: ToastrService,
    private permService: PermissionsService,
    private router: Router
  ) {
    this.buildForms();
  }

  ngOnInit(): void {
    const userDetail: any = this._utilityService.decodeUserCode();
    this.userType = userDetail?.user_type ?? null;
    this.initComponent();
  }

  async initComponent() {
    this.loader = true;
    try {
      await this.getComponentPermissions();
      // One subscription only — the ticket list registers this twice, which
      // makes every keystroke fire two requests.
      this.searchSubject.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
        this.searchElement = searchTerm;
        this.updateQueryParams({ search: this.searchElement });
      });
      if (this.permissions.canView) {
        this.loadDropdowns();
        this.getTableData();
      }
    } finally {
      // Always cleared, so a user without view permission sees the no-access
      // card rather than a spinner that never stops.
      this.loader = false;
    }
  }

  async getComponentPermissions() {
    const url = this.permService.getCurrentUrl();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();
    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
        canAdd: !!matchedMenu.canAdd,
        canEdit: !!matchedMenu.canEdit,
        canView: !!matchedMenu.canView,
        canDelete: !!matchedMenu.canDelete,
      }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }

  // -- forms -----------------------------------------------------------

  private buildForms(): void {
    this.fsrForm = this.formBuilder.group({
      plant: [null, Validators.required],
      customer: [null],
      // Display-only, mirrored from the selected plant. Disabled so they
      // stay out of .value and are never posted — the backend derives the
      // city from the plant FK.
      region: [{ value: '', disabled: true }],
      city: [{ value: '', disabled: true }],
      job_visit_type: [null, Validators.required],
      // An installation can mix panel types, so this is a repeatable
      // (No. of Panels x Panel Capacity) row rather than one scalar pair.
      panel_groups: this.formBuilder.array([this.buildPanelGroup()]),
      // Derived — disabled so it cannot be posted, and excluded on submit.
      system_capacity_dc: [{ value: '', disabled: true }],
      assigned_to: [null],
      visit_date: [null],
      time_in: [null],
      time_out: [null],
      total_worked_hours: [{ value: '', disabled: true }],
      description_of_job: [null, Validators.maxLength(2000)],
      complaint_type: [null],
      check_module_cleaning: [false],
      check_loose_connections: [false],
      check_inverter_cleaning: [false],
      check_module_damage: [false],
      repl_broken_panel: [false],
      repl_mc4: [false],
      repl_dc_cable: [false],
      repl_ac_cable: [false],
      repl_others: [false],
      repl_others_detail: [null, Validators.maxLength(500)],
      remarks: [null, Validators.maxLength(2000)],
    }, { validators: timePairValidator });

    this.assignForm = this.formBuilder.group({
      assignee_id: [null, Validators.required],
    });

    this.actionForm = this.formBuilder.group({
      reason: [null, [Validators.required, Validators.maxLength(1000)]],
    });

    // Total Panel Capacity = Σ (No. of Panels x Panel Capacity). The array's
    // valueChanges fires on any row's edit, add, or remove.
    (this.fsrForm.get('panel_groups') as FormArray).valueChanges.subscribe(() =>
      this.recomputeSystemCapacity()
    );

    // Total worked hours from Time In / Time Out.
    ['time_in', 'time_out'].forEach((field) => {
      this.fsrForm.get(field)?.valueChanges.subscribe(() => this.recomputeWorkedHours());
    });

    // "Others" only needs a description when it is actually ticked.
    this.fsrForm.get('repl_others')?.valueChanges.subscribe((checked: boolean) => {
      const control = this.fsrForm.get('repl_others_detail');
      if (checked) {
        control?.addValidators(Validators.required);
      } else {
        control?.removeValidators(Validators.required);
        control?.setValue(null);
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private buildPanelGroup(overrides: { number_of_panels?: number | null; panel_capacity?: number | null } = {}): FormGroup {
    return this.formBuilder.group({
      number_of_panels: [overrides.number_of_panels ?? null, [Validators.required, Validators.min(1)]],
      panel_capacity: [overrides.panel_capacity ?? null, [Validators.required, Validators.min(0.001)]],
    });
  }

  addPanelGroup(): void {
    (this.fsrForm.get('panel_groups') as FormArray).push(this.buildPanelGroup());
  }

  removePanelGroup(index: number): void {
    const array = this.fsrForm.get('panel_groups') as FormArray;
    if (array.length > 1) {
      array.removeAt(index);
    }
  }

  private recomputeSystemCapacity(): void {
    const groups = (this.fsrForm.get('panel_groups') as FormArray).value as Array<{
      number_of_panels: unknown;
      panel_capacity: unknown;
    }>;
    let total = 0;
    let hasValidRow = false;
    for (const group of groups) {
      // Decimals come back from the API as strings, so coerce before multiplying.
      const panels = Number(group.number_of_panels);
      const capacity = Number(group.panel_capacity);
      if (Number.isFinite(panels) && Number.isFinite(capacity) && panels > 0 && capacity > 0) {
        total += panels * capacity;
        hasValidRow = true;
      }
    }
    const value = hasValidRow ? total.toFixed(3) : '';
    this.fsrForm.get('system_capacity_dc')?.setValue(value, { emitEvent: false });
  }

  private recomputeWorkedHours(): void {
    const value = workedHours(
      this.fsrForm.get('time_in')?.value,
      this.fsrForm.get('time_out')?.value
    );
    this.fsrForm.get('total_worked_hours')?.setValue(value, { emitEvent: false });
  }

  // -- dropdowns -------------------------------------------------------

  private loadDropdowns(): void {
    this._utilityService.fetchStationInfo(null, null).subscribe((res: any) => {
      this.plantList = res?.data || [];
    });
    this._utilityService.getFsrStatusList().subscribe((res: any) => {
      this.statusFilterList = res || [];
    });
    this._utilityService.assignedTicketUsersFsr().subscribe((res: any) => {
      this.assignedUsersFilter = res || [];
    });
    this._utilityService.getFsrJobVisitTypes().subscribe((res: any) => {
      this.jobVisitTypes = res || [];
    });
    this._utilityService.getFsrComplaintTypes().subscribe((res: any) => {
      this.complaintTypes = res || [];
    });
    // The service already unwraps data.response to a bare array.
    this._utilityService.getCitiesDropDown().subscribe((res: any) => {
      this.cityList = res || [];
    });
  }

  /**
   * Load the plant's customers and ZC staff.
   *
   * electrician=0 returns the Customer-type owners, electrician=1 the
   * Electrician-type ones. Both args are always passed explicitly: the service
   * guards on `!== null`, so an omitted arg would send `electrician=undefined`
   * and the backend's int() cast would 500.
   */
  onPlantChange(plant: any): void {
    const plantId = plant?.id ?? null;
    this.customerList = [];
    this.staffList = [];
    this.fsrForm.patchValue({ customer: null, assigned_to: null }, { emitEvent: false });

    // ng-select's (change) hands back the whole item, and the plant list comes
    // from /stations/owner-dropdown/ which already carries city and region
    // names — so these need no extra request.
    this.setPlantLocation(plant);

    if (!plantId) {
      return;
    }

    this._utilityService.fetchStationInfo(plantId, 0).subscribe((res: any) => {
      this.customerList = res?.data?.[0]?.owners || [];
      // A single customer needs no choosing — select it and show it read-only.
      if (this.customerList.length === 1) {
        this.fsrForm.patchValue({ customer: this.customerList[0].id }, { emitEvent: false });
      }
    });

    this._utilityService.fetchStationInfo(plantId, 1).subscribe((res: any) => {
      this.staffList = res?.data?.[0]?.owners || [];
    });
  }

  /** Mirror the selected plant's city/region into the read-only display fields. */
  private setPlantLocation(station: any): void {
    this.fsrForm.patchValue(
      { region: station?.region || '', city: station?.city || '' },
      { emitEvent: false }
    );
  }

  get customerFieldState(): 'none' | 'single' | 'multiple' {
    if (!this.customerList.length) {
      return 'none';
    }
    return this.customerList.length === 1 ? 'single' : 'multiple';
  }

  get selectedCustomerName(): string {
    const id = this.fsrForm.get('customer')?.value;
    return this.customerList.find((owner) => owner.id === id)?.full_name || '';
  }

  // -- listing ---------------------------------------------------------

  private updateQueryParams(paramsToUpdate: any): void {
    this.latestQueryParams = { ...this.latestQueryParams, ...paramsToUpdate };

    if (this.latestQueryParams.search === '') {
      this.latestQueryParams.search = null;
    }

    FILTER_KEYS.concat(['sort', 'column', 'search']).forEach((key) => {
      if (this.latestQueryParams[key] === null || this.latestQueryParams[key] === undefined) {
        delete this.latestQueryParams[key];
      }
    });

    // Any change to the filters or page size resets to page 1, otherwise the
    // user can land on a page that no longer exists.
    if (
      paramsToUpdate.page === undefined &&
      FILTER_KEYS.concat(['search', 'size']).some((key) => key in paramsToUpdate)
    ) {
      this.latestQueryParams.page = 1;
    }

    this.getTableData();
  }

  private getTableData(): void {
    const params = this.latestQueryParams;
    this.fetchFsrList({
      search: params['search'] || this.searchElement,
      page: params['page'] ? +params['page'] : 1,
      size: params['size'] ? +params['size'] : 10,
      sort: params['sort'] ?? null,
      column: params['column'] ?? null,
      assignee: params['assignee'] ?? null,
      status: params['status'] ?? null,
      origin: params['origin'] ?? null,
      job_visit_type: params['job_visit_type'] ?? null,
      city: params['city'] ?? null,
    });
  }

  fetchFsrList(filters: any): void {
    const size = filters.size || 10;
    this.isLoadingTable = true;
    this._utilityService
      .getFsrList(filters)
      .pipe(finalize(() => (this.isLoadingTable = false)))
      .subscribe({
        next: (res: any) => {
          if (res?.results) {
            this.fsrList = res.results.map((fsr: FSR, index: number) => ({
              ...fsr,
              srNo: (res.pagination.current_page - 1) * size + index + 1,
            }));
            this.current_page = res.pagination.current_page;
            this.next_page = res.pagination.next_page;
            this.previous_page = res.pagination.previous_page;
            this.total_pages = res.pagination.total_pages;
            this.total_items = res.pagination.total_records;
          } else {
            this.fsrList = [];
          }
        },
        error: (err) => {
          this.fsrList = [];
          this.handleApiFieldErrors(err, this.toaster);
        },
      });
  }

  public searchData(event: Event): void {
    this.searchElement = (event.target as HTMLInputElement)?.value || '';
    this.searchSubject.next(this.searchElement);
  }

  public selectStatus(status: any): void {
    this.updateQueryParams({ status: status ?? null });
  }

  public selectAssignee(assignee: any): void {
    this.updateQueryParams({ assignee: assignee ?? null });
  }

  public selectJobType(jobVisitType: any): void {
    this.updateQueryParams({ job_visit_type: jobVisitType ?? null });
  }

  public selectCity(city: any): void {
    this.updateQueryParams({ city: city ?? null });
  }

  onSort(event: any): void {
    const sortBy = event.sorts[0];
    this.updateQueryParams({ sort: sortBy.dir === 'asc' ? '1' : '-1', column: sortBy.prop });
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.total_pages) {
      this.updateQueryParams({ page });
    }
  }

  navigatePage(direction: string): void {
    if (direction === 'prev' && this.current_page > 1) {
      this.goToPage(this.current_page - 1);
    } else if (direction === 'next' && this.next_page !== null) {
      this.goToPage(this.current_page + 1);
    }
  }

  navigateToPage(): void {
    if (this.pageToGo >= 1 && this.pageToGo <= this.total_pages) {
      this.goToPage(this.pageToGo);
    }
  }

  public changePageSize(): void {
    this.pageSize = this.pageSizeControl.value!;
    this.updateQueryParams({ size: this.pageSize });
  }

  /** "City, Region" for the Location cell, skipping whichever is missing. */
  locationSubtitle(row: FSR): string {
    return [row?.plant_city, row?.plant_region].filter(Boolean).join(', ');
  }

  normalizeStatus(status: string): string {
    return (status || '').toLowerCase().trim().replace(/\s+/g, ' ');
  }

  getStatusBadgeClass(status: string): string {
    if (!status) {
      return '';
    }
    return this.statusBadgeMap[this.normalizeStatus(status)] || 'badge-blue';
  }

  /**
   * Always the server's status. is_approved is a separate flag — an approved FSR
   * can still be Closed / Resolved / Reopened — so it must not override the label.
   */
  statusLabel(row: FSR): string {
    return row?.status_display;
  }

  statusBadge(row: FSR): string {
    return this.getStatusBadgeClass(row?.status_display);
  }

  viewFsr(id: number): void {
    this.router.navigate(['/help-desk/fsr', id]);
  }

  // -- action visibility ----------------------------------------------

  canShowAction(row: FSR, action: FSRAction): boolean {
    return row?.user_permissions?.[action] === true && row?.fsr_actions?.includes(action);
  }

  /**
   * Approve is additionally limited to the designated engineer. The backend's
   * authoritative gate is the `approve` config flag; this narrows the button.
   */
  /** Drives the kebab menu: hidden entirely when no status action applies to the row. */
  hasStatusActions(row: FSR): boolean {
    const actions: FSRAction[] = ['assign', 'start', 'on_hold', 'pending_material', 'resolve', 'close', 're_open', 'cancel'];
    return this.canApprove(row) || actions.some((action) => this.canShowAction(row, action));
  }

  canApprove(row: FSR): boolean {
    const isApprover =
      this.userType === FSR_APPROVER_USER_TYPE || this.userType === 'superuser';
    return isApprover && !row?.is_approved && this.canShowAction(row, 'approve');
  }

  // -- create / edit ---------------------------------------------------

  addNewFsr(content: any): void {
    this.isEditMode = false;
    this.selectedFsrId = null;
    this.selectedFsr = null;
    this.resetFsrForm();
    this.openModal(content, { size: 'fullscreen' });
  }

  editFsr(content: any, row: FSR): void {
    this.isEditMode = true;
    this.selectedFsrId = row.id;
    this.selectedFsr = row;
    this.resetFsrForm();

    const panelGroupsArray = this.fsrForm.get('panel_groups') as FormArray;
    panelGroupsArray.clear();
    const rowGroups = row.panel_groups?.length ? row.panel_groups : [{ number_of_panels: null, panel_capacity: null }];
    rowGroups.forEach((group) => {
      panelGroupsArray.push(this.buildPanelGroup({
        number_of_panels: group.number_of_panels,
        panel_capacity: group.panel_capacity !== null ? Number(group.panel_capacity) : null,
      }));
    });

    this.fsrForm.patchValue({
      plant: row.plant?.id ?? null,
      job_visit_type: row.job_visit_type,
      assigned_to: row.assigned_to?.id ?? null,
      visit_date: row.visit_date,
      time_in: row.time_in,
      time_out: row.time_out,
      description_of_job: row.description_of_job,
      complaint_type: row.complaint_type,
      check_module_cleaning: row.check_module_cleaning,
      check_loose_connections: row.check_loose_connections,
      check_inverter_cleaning: row.check_inverter_cleaning,
      check_module_damage: row.check_module_damage,
      repl_broken_panel: row.repl_broken_panel,
      repl_mc4: row.repl_mc4,
      repl_dc_cable: row.repl_dc_cable,
      repl_ac_cable: row.repl_ac_cable,
      repl_others: row.repl_others,
      repl_others_detail: row.repl_others_detail,
      remarks: row.remarks,
    });

    this.existingAttachments = row.attachments || [];

    // Load the plant's owner lists, then restore the saved customer: the
    // request is async, so patching before it lands would be overwritten.
    const plantId = row.plant?.id ?? null;
    if (plantId) {
      const forFsrId = row.id;
      this._utilityService.fetchStationInfo(plantId, 0).subscribe((res: any) => {
        // A second row may have been opened while this was in flight.
        if (this.selectedFsrId !== forFsrId) {
          return;
        }
        this.customerList = res?.data?.[0]?.owners || [];
        this.setPlantLocation(res?.data?.[0]);
        this.fsrForm.patchValue({ customer: row.customer }, { emitEvent: false });
      });
      this._utilityService.fetchStationInfo(plantId, 1).subscribe((res: any) => {
        if (this.selectedFsrId !== forFsrId) {
          return;
        }
        this.staffList = res?.data?.[0]?.owners || [];
      });
    }

    this.openModal(content, { size: 'fullscreen' });
  }

  save(warningModal: any): void {
    if (this.fsrForm.invalid) {
      this.fsrForm.markAllAsTouched();
      this.toaster.error('Please fill all required fields correctly.');
      return;
    }
    if (this.customerFieldState === 'multiple' && !this.fsrForm.get('customer')?.value) {
      this.toaster.error('Please select a customer.');
      return;
    }

    // Warn about an oversized batch before sending anything over the wire.
    if (totalSize(this.files) > MAX_UPLOAD_BYTES) {
      this.oversizeBreakdown = categoriseFiles(this.files);
      this.oversizeModalRef = this.modalService.open(warningModal, {
        centered: true,
        backdrop: 'static',
      });
      return;
    }

    this.submitFsr(false);
  }

  /** "Yes, compress" on the oversize warning. */
  confirmCompressAndUpload(): void {
    this.oversizeModalRef?.close('compress');
    this.oversizeModalRef = null;
    this.submitFsr(true);
  }

  /** "No" on the oversize warning — nothing is uploaded, the form stays filled. */
  cancelOversizeUpload(): void {
    this.oversizeModalRef?.dismiss('cancel');
    this.oversizeModalRef = null;
    this.oversizeBreakdown = null;
  }

  private submitFsr(compress: boolean): void {
    const formData = this.buildFsrFormData(compress);

    this.isSaving = true;
    const request$ = this.isEditMode
      ? this._utilityService.updateFsr(this.selectedFsrId!, formData)
      : this._utilityService.createFsr(formData);

    request$.pipe(finalize(() => (this.isSaving = false))).subscribe({
      next: (res: any) => {
        this.toaster.success(this.isEditMode ? 'FSR updated successfully' : 'FSR created successfully');
        // The FSR saved even when its attachments did not — say so rather than
        // letting the user believe the files went up.
        if (res?.attachment_warning) {
          this.toaster.warning(res.attachment_warning, 'Attachments not saved', {
            timeOut: 12000,
            closeButton: true,
          });
        }
        this.closeModal();
        this.getTableData();
      },
      error: (err) => this.handleApiFieldErrors(err, this.toaster),
    });
  }

  /**
   * Shared by this component and the ticket module's FSR modal.
   * Skips the two derived display fields and any empty optional value.
   */
  buildFsrFormData(compress: boolean): FormData {
    const raw = this.fsrForm.getRawValue();
    const formData = new FormData();

    const skip = new Set(['system_capacity_dc', 'total_worked_hours', 'region', 'city']);
    Object.keys(raw).forEach((key) => {
      if (skip.has(key)) {
        return;
      }
      const value = raw[key];
      if (value === null || value === undefined || value === '') {
        return;
      }
      // Multipart can't carry a native nested array — the backend parses
      // this as a JSON-encoded string (see parse_panel_groups in fsr_management.py).
      if (key === 'panel_groups') {
        formData.append(key, JSON.stringify(value));
        return;
      }
      formData.append(key, value as any);
    });

    this.files.forEach((file) => formData.append('attachments', file, file.name));
    if (compress) {
      formData.append('compress', 'true');
    }
    return formData;
  }

  // -- attachments -----------------------------------------------------

  /**
   * Files are only collected here; the 100MB check runs on submit so the user
   * sees one warning for the whole batch rather than one per picker interaction.
   * Fed by both the file picker and the dropzone.
   */
  addFsrFiles(selected: FileList): void {
    if (!selected?.length) {
      return;
    }
    for (let i = 0; i < selected.length; i++) {
      this.files.push(selected[i]);
    }
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
  }

  // -- lifecycle actions -----------------------------------------------

  openAction(content: any, row: FSR, action: FSRAction): void {
    this.selectedFsrId = row.id;
    this.selectedFsr = row;
    this.pendingAction = action;

    this.actionForm.reset();
    this.assignForm.reset();

    if (action === 'assign') {
      this.staffList = [];
      const forFsrId = row.id;
      this._utilityService.fetchStationInfo(row.plant?.id ?? null, 1).subscribe((res: any) => {
        if (this.selectedFsrId !== forFsrId) {
          return;
        }
        this.staffList = res?.data?.[0]?.owners || [];
      });
    }

    this.openModal(content, { centered: true, backdrop: 'static', keyboard: false });
  }

  assignFsr(): void {
    if (this.assignForm.invalid) {
      this.assignForm.markAllAsTouched();
      this.toaster.error('Please select a user');
      return;
    }
    this.runAction(
      this._utilityService.assignFsr(this.selectedFsrId!, {
        assignee_id: this.assignForm.get('assignee_id')?.value,
      }),
      'FSR assigned successfully'
    );
  }

  startFsr(): void {
    this.runAction(this._utilityService.startFsr(this.selectedFsrId!), 'FSR started successfully');
  }

  resolveFsr(): void {
    this.runAction(this._utilityService.resolveFsr(this.selectedFsrId!), 'FSR resolved successfully');
  }

  approveFsr(): void {
    this.runAction(
      this._utilityService.approveFsr(this.selectedFsrId!),
      'FSR approved and the customer has been notified'
    );
  }

  /**
   * Submit whichever reason-based action the modal was opened for.
   *
   * The five reason modals share one body (see #reasonBody), so the endpoint is
   * picked from `pendingAction` rather than passed through the template.
   */
  submitPendingAction(): void {
    if (this.actionForm.invalid) {
      this.actionForm.markAllAsTouched();
      this.toaster.error('Please provide a reason');
      return;
    }

    const id = this.selectedFsrId!;
    const payload = { reason: this.actionForm.get('reason')?.value };

    switch (this.pendingAction) {
      case 'on_hold':
        return this.runAction(this._utilityService.holdFsr(id, payload), 'FSR put on hold successfully');
      case 'pending_material':
        return this.runAction(this._utilityService.pendingFsr(id, payload), 'FSR marked as pending material');
      case 'close':
        return this.runAction(this._utilityService.closeFsr(id, payload), 'FSR closed successfully');
      case 're_open':
        return this.runAction(this._utilityService.reopenFsr(id, payload), 'FSR reopened successfully');
      case 'cancel':
        return this.runAction(this._utilityService.cancelFsr(id, payload), 'FSR cancelled successfully');
      default:
        this.toaster.error('Unknown action. Please reopen the dialog and try again.');
    }
  }

  private runAction(request$: any, successMessage: string): void {
    this.loader = true;
    request$.pipe(finalize(() => (this.loader = false))).subscribe({
      next: () => {
        this.toaster.success(successMessage);
        this.closeModal();
        this.getTableData();
      },
      error: (err: HttpErrorResponse) => this.handleApiFieldErrors(err, this.toaster),
    });
  }

  // -- modal plumbing --------------------------------------------------

  private openModal(content: any, options: any): void {
    this.modalRef = this.modalService.open(content, options);
    // Clear state on dismiss too, not just on an explicit close.
    this.modalRef.hidden.subscribe(() => {
      this.modalRef = null;
      this.pendingAction = null;
    });
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.close('Closed');
      this.modalRef = null;
    }
    this.selectedFsr = null;
    this.selectedFsrId = null;
    this.pendingAction = null;
    this.resetFsrForm();
  }

  resetFsrForm(): void {
    this.fsrForm.reset({
      check_module_cleaning: false,
      check_loose_connections: false,
      check_inverter_cleaning: false,
      check_module_damage: false,
      repl_broken_panel: false,
      repl_mc4: false,
      repl_dc_cable: false,
      repl_ac_cable: false,
      repl_others: false,
    });
    // .reset() clears each row's value but leaves the array's length alone —
    // collapse back to exactly one empty row.
    const panelGroupsArray = this.fsrForm.get('panel_groups') as FormArray;
    panelGroupsArray.clear();
    panelGroupsArray.push(this.buildPanelGroup());
    this.assignForm.reset();
    this.actionForm.reset();
    this.files = [];
    this.existingAttachments = [];
    this.customerList = [];
    this.staffList = [];
    this.oversizeBreakdown = null;
  }

  handleApiFieldErrors(error: HttpErrorResponse, toastr: ToastrService): void {
    if (error.error?.status && Array.isArray(error.error.status)) {
      error.error.status.forEach((msg: string) => toastr.error(msg));
      return;
    }
    if (error.error?.error && typeof error.error.error === 'string') {
      toastr.error(error.error.error);
      return;
    }
    if (error.error?.message) {
      toastr.error(error.error.message);
      return;
    }
    if (error.error?.errors) {
      const errors = error.error.errors;
      Object.keys(errors).forEach((key) => {
        toastr.error(Array.isArray(errors[key]) ? errors[key][0] : errors[key]);
      });
      return;
    }
    // DRF field errors arrive as a bare {field: [msg]} object.
    if (error.error && typeof error.error === 'object' && !Array.isArray(error.error)) {
      const keys = Object.keys(error.error);
      if (keys.length) {
        keys.forEach((key) => {
          const value = (error.error as any)[key];
          toastr.error(Array.isArray(value) ? `${value[0]}` : `${value}`);
        });
        return;
      }
    }
    toastr.error('Sorry, something went wrong. Please try again.');
  }
}
